/**
 * ServiceOrderInline
 *
 * Desktop-native version of ServiceOrderModal — same logic and state but
 * renders inline (no IonModal) as a set of styled sections inside a card.
 */
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { IonIcon, IonSpinner } from '@ionic/react';
import {
  addCircleOutline, trashOutline, receiptOutline,
  settingsOutline, chevronDownOutline,
} from 'ionicons/icons';
import GlassPicker from './GlassPicker';
import LineItemInline from './LineItemInline';
import { type ServiceOrderDraft, type ServiceOrderLineItem } from './ServiceOrderModal';
import {
  fetchZohoAll, fetchZohoPricelistItems, resetSoNumberSequence,
  type ZohoItem, type ZohoTax,
} from '../services/zohoServiceOrderService';
import type { ZohoPricelist } from '../services/zohoServiceOrderService';
import { getCachedContact } from '@/utils/contactCache';
import { zipToPricelistCode } from '@/utils/pricelists';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { dbContacts } from '../auth/firebase';
import { doc, getDoc } from 'firebase/firestore';
import styles from './ServiceOrderInline.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAYMENT_TERMS = [
  { get label() { return i18n.t('components.ServiceOrderInline.dueOnReceipt'); }, days: 0 },
  { get label() { return i18n.t('components.ServiceOrderInline.net3'); },          days: 3 },
  { get label() { return i18n.t('components.ServiceOrderInline.net7'); },          days: 7 },
  { get label() { return i18n.t('components.ServiceOrderInline.net15'); },         days: 15 },
  { get label() { return i18n.t('components.ServiceOrderInline.net30'); },         days: 30 },
  { get label() { return i18n.t('components.ServiceOrderInline.net45'); },         days: 45 },
  { get label() { return i18n.t('components.ServiceOrderInline.net60'); },         days: 60 },
  { get label() { return i18n.t('components.ServiceOrderInline.custom'); },         days: -1 },
];


function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function subTotal(items: ServiceOrderLineItem[]): number {
  return items.reduce((s, i) => {
    const lineAmt = i.quantity * i.rate;
    const discountAmt = i.discount
      ? i.discountType === '%' ? lineAmt * (i.discount / 100) : i.discount
      : 0;
    return s + lineAmt - discountAmt;
  }, 0);
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ServiceOrderInlineProps {
  onConfirm: (draft: ServiceOrderDraft) => void;
  customerName?: string;
  contactId?: string;
  bookingId?: string;
  soNumberError?: string;
  initialDraft?: ServiceOrderDraft | null;
  /** YYYY-MM-DD used as the SO date for new orders (e.g. the booking date); defaults to today */
  defaultDate?: string;
  /** Label for the primary action button — e.g. "Save" when editing an existing order */
  confirmLabel?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ServiceOrderInline: React.FC<ServiceOrderInlineProps> = ({
  onConfirm,
  customerName = '',
  contactId = '',
  bookingId = '',
  soNumberError,
  initialDraft,
  defaultDate,
  confirmLabel = 'Confirm Order',
}) => {
  const { t: tr } = useTranslation();
  const [soDate, setSoDate]                       = useState(todayISO());
  const [soNumber, setSoNumber]                   = useState('');
  const [soNumberMode, setSoNumberMode]           = useState<'auto' | 'manual-each' | 'manual-once'>('auto');
  const [soNumberAlertOpen, setSoNumberAlertOpen] = useState(false);
  const [nextSoNumber, setNextSoNumber]           = useState('');
  const [paymentTermsDays, setPaymentTermsDays]   = useState(3);
  const [customTermDays, setCustomTermDays]       = useState(0);
  const [serviceMonth, setServiceMonth]           = useState('');
  const [isInclusiveTax, setIsInclusiveTax]       = useState(true);
  const [pricelistId, setPricelistId]             = useState('');
  const [zohoItems, setZohoItems]                 = useState<ZohoItem[]>([]);
  const [baseZohoItems, setBaseZohoItems]         = useState<ZohoItem[]>([]);
  const [loadingItems, setLoadingItems]           = useState(false);
  const [zohoTaxes, setZohoTaxes]                 = useState<ZohoTax[]>([]);
  const [loadingTaxes, setLoadingTaxes]           = useState(false);
  const [serviceMonths, setServiceMonths]         = useState<string[]>([]);
  const [serviceMonthsFromZoho, setServiceMonthsFromZoho] = useState(false);
  const serviceMonthCfIdRef                       = useRef('');
  const zohoTaxesRef                              = useRef<ZohoTax[]>([]);
  const [lineItemModalOpen, setLineItemModalOpen] = useState(false);
  const [editingIndex, setEditingIndex]           = useState<number | null>(null);
  const [termsDropOpen, setTermsDropOpen]         = useState(false);
  const [termsDropPos, setTermsDropPos]           = useState({ top: 0, left: 0, width: 0 });
  const termsDropWrapRef                          = useRef<HTMLDivElement>(null);
  const termsDropTriggerRef                       = useRef<HTMLDivElement>(null);
  const termsDropListRef                          = useRef<HTMLDivElement>(null);
  const [monthDropOpen, setMonthDropOpen]         = useState(false);
  const [monthDropPos, setMonthDropPos]           = useState({ top: 0, left: 0, width: 0 });
  const monthDropWrapRef                          = useRef<HTMLDivElement>(null);
  const monthDropTriggerRef                       = useRef<HTMLDivElement>(null);
  const monthDropListRef                          = useRef<HTMLDivElement>(null);
  const [pricelists, setPricelists]               = useState<ZohoPricelist[]>([]);
  const [loadingPricelists, setLoadingPricelists] = useState(false);
  const [lineItems, setLineItems]                 = useState<ServiceOrderLineItem[]>([]);
  const [soNotes, setSoNotes]                     = useState('');
  const [soTerms, setSoTerms]                     = useState('');
  const [adjustment, setAdjustment]               = useState('');
  const [loading, setLoading]                     = useState(false);

  // Snapshot of props the mount-only reset/init effect below reads without
  // becoming reactive to every one of their changes — this component is
  // reused in place by its callers (no key prop forces a remount), and
  // initialDraft in particular is a fresh object literal every render of
  // those callers, so depending on it directly would re-run the whole
  // reset-and-refetch on every parent render.
  const latestForInitEffectRef = useRef({ contactId, initialDraft, defaultDate });
  useEffect(() => {
    latestForInitEffectRef.current = { contactId, initialDraft, defaultDate };
  });

  // Initialise from draft and fetch Zoho data once on mount. Deferred via a
  // resolved-promise microtask (not called synchronously in the effect
  // body) — this only ever runs once (deps []), so the deferral is purely
  // to satisfy the effect-setState rule, not to change behavior.
  useEffect(() => { Promise.resolve().then(() => {
    const { contactId, initialDraft, defaultDate } = latestForInitEffectRef.current;
    setSoDate(defaultDate || todayISO());
    setSoNumber('');
    setSoNumberMode('auto');
    setPaymentTermsDays(3);
    setCustomTermDays(0);
    setIsInclusiveTax(true);
    setPricelistId('');
    setLineItems([]);
    setSoNotes('');
    setSoTerms('');
    setAdjustment('');

    if (initialDraft) {
      if (initialDraft.soDate) setSoDate(initialDraft.soDate);
      if (initialDraft.soNumber) { setSoNumber(initialDraft.soNumber); setSoNumberMode('manual-once'); }
      if (initialDraft.paymentTerms !== undefined) setPaymentTermsDays(initialDraft.paymentTerms);
      if (initialDraft.serviceMonth) setServiceMonth(initialDraft.serviceMonth);
      setIsInclusiveTax(initialDraft.isInclusiveTax ?? true);
      if (initialDraft.pricelistId) setPricelistId(initialDraft.pricelistId);
      if (initialDraft.lineItems?.length) setLineItems(initialDraft.lineItems);
      if (initialDraft.notes) setSoNotes(initialDraft.notes);
      if (initialDraft.terms) setSoTerms(initialDraft.terms);
      if (initialDraft.adjustment !== undefined) setAdjustment(String(initialDraft.adjustment));
    }

    setLoading(true);
    setLoadingItems(true);
    setLoadingTaxes(true);
    setLoadingPricelists(true);

    const svc = DataServiceFactory.getService();
    const shouldAutoPopulate = !!contactId && !initialDraft;

    Promise.allSettled([
      fetchZohoAll(),
      shouldAutoPopulate ? getCachedContact(contactId, () => svc.contacts.getContactById(contactId)) : Promise.resolve(null),
      shouldAutoPopulate ? getDoc(doc(dbContacts, 'contactBilling', contactId)) : Promise.resolve(null),
    ]).then(([zohoSettled, contactSettled, billingSettled]) => {
      if (zohoSettled.status !== 'fulfilled') {
        console.error('[SO Inline] fetchZohoAll failed:', zohoSettled.reason);
        return;
      }
      const all = zohoSettled.value;
      setZohoItems(all.items);
      setBaseZohoItems(all.items);
      setZohoTaxes(all.taxes);
      zohoTaxesRef.current = all.taxes;
      setPricelists(all.pricelists);
      const months = all.serviceMonthOptions;
      setServiceMonths(months);
      setServiceMonthsFromZoho(months.length > 0);
      const currentMonthName = new Date().toLocaleDateString('en-US', { month: 'long' }); // i18n-ignore: saved to Zoho, stays English
      const defaultMonth = months.find((m: string) => m === currentMonthName) ?? months[0] ?? '';
      if (!initialDraft?.serviceMonth) setServiceMonth(defaultMonth);
      serviceMonthCfIdRef.current = all.serviceMonthCustomFieldId ?? '';
      if (all.defaults.nextSoNumber) setNextSoNumber(all.defaults.nextSoNumber.replace(/^SO-/i, ''));
      if (!initialDraft?.notes && all.defaults.notes) setSoNotes(all.defaults.notes);
      if (!initialDraft?.terms && all.defaults.terms) setSoTerms(all.defaults.terms);

      if (!shouldAutoPopulate) return;

      const contactResult = contactSettled.status === 'fulfilled' ? contactSettled.value : null;
      const billingSnap   = billingSettled.status === 'fulfilled' ? billingSettled.value : null;
      const billing = billingSnap && 'exists' in billingSnap && billingSnap.exists() ? billingSnap.data() : null;

      const defaultTaxId = zohoTaxesRef.current.find(t => t.is_tax_group && Math.abs(t.tax_percentage - 10.55) < 0.01)?.tax_id;

      // Pricelist: billing doc wins, fallback to zip lookup
      if (billing?.defaultPricelistId) {
        setPricelistId(billing.defaultPricelistId);
      } else {
        const addresses = (contactResult as { data?: { addresses?: { primary?: boolean; address?: { postalCode?: string } }[] } } | null)?.data?.addresses ?? [];
        const primary = addresses.find((a) => a.primary) ?? addresses[0];
        const zip = primary?.address?.postalCode;
        const code = zip ? zipToPricelistCode(zip) : null;
        const match = code ? all.pricelists.find(p => p.name === code) : null;
        if (match) setPricelistId(match.pricelist_id);
      }

      if (billing?.isInclusiveTax !== undefined) setIsInclusiveTax(billing.isInclusiveTax);

      if (billing?.defaultItemId) {
        const item = all.items.find((i: ZohoItem) => i.item_id === billing.defaultItemId);
        if (item) {
          // See ServiceOrderModal.tsx's matching comment / functions/src/services
          // /zohoServiceOrderService.ts's finalPriceToRate for the full reasoning:
          // billing.defaultRate is always "the final price the customer pays,"
          // not the Zoho line-item rate. Only back-calculate to a pre-tax rate
          // for Exclusive + Helcim-subscriber clients; everyone else uses it as-is.
          const isSubscriberExclusive = billing.isInclusiveTax === false && billing.isHelcimSubscriber === true;
          const taxPct = typeof billing.taxPercentage === 'number' ? billing.taxPercentage : 0;
          const rawRate = billing.defaultRate != null && billing.defaultRate > 0 ? billing.defaultRate : item.rate;
          const rate = (isSubscriberExclusive && taxPct > 0 && billing.defaultRate != null && billing.defaultRate > 0)
            ? Math.round((rawRate / (1 + taxPct / 100)) * 100) / 100
            : rawRate;
          setLineItems([{
            item_id: item.item_id,
            name: item.name,
            description: item.description ?? '',
            quantity: 1,
            rate,
            tax_id: defaultTaxId,
            tax_name: zohoTaxesRef.current.find(t => t.tax_id === defaultTaxId)?.tax_name,
          }]);
        }
      }
    }).finally(() => {
      setLoading(false);
      setLoadingItems(false);
      setLoadingTaxes(false);
      setLoadingPricelists(false);
    });
  }); }, []);

  // Clearing pricelist items when no pricelist is selected is computed
  // during render (React's "adjust state when a dependency changes"
  // pattern) rather than as a synchronous setState call at the top of the
  // effect, which now only handles the actual async fetch.
  const [prevPricelistId, setPrevPricelistId] = useState(pricelistId);
  if (pricelistId !== prevPricelistId) {
    setPrevPricelistId(pricelistId);
    if (!pricelistId) setZohoItems(baseZohoItems);
  }
  useEffect(() => {
    if (!pricelistId) return;
    fetchZohoPricelistItems(pricelistId)
      .then(plItems => {
        if (!plItems.length) return;
        const rateMap = new Map(plItems.map(i => [i.item_id, i.rate]));
        setZohoItems(baseZohoItems.map(i => rateMap.has(i.item_id) ? { ...i, rate: rateMap.get(i.item_id)! } : i));
      })
      .catch(() => setZohoItems(baseZohoItems));
  }, [pricelistId, baseZohoItems]);

  useEffect(() => {
    if (!monthDropOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!monthDropWrapRef.current?.contains(t) && !monthDropListRef.current?.contains(t)) {
        setMonthDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [monthDropOpen]);

  const handleMonthDropOpen = () => {
    if (monthDropTriggerRef.current) {
      const r = monthDropTriggerRef.current.getBoundingClientRect();
      setMonthDropPos({ top: r.bottom - 2, left: r.left, width: r.width });
    }
    setMonthDropOpen(v => !v);
  };

  useEffect(() => {
    if (!termsDropOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!termsDropWrapRef.current?.contains(t) && !termsDropListRef.current?.contains(t)) {
        setTermsDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [termsDropOpen]);

  const handleTermsDropOpen = () => {
    if (termsDropTriggerRef.current) {
      const r = termsDropTriggerRef.current.getBoundingClientRect();
      setTermsDropPos({ top: r.bottom - 2, left: r.left, width: r.width });
    }
    setTermsDropOpen(v => !v);
  };

  const openAddItem  = () => { setEditingIndex(null); setLineItemModalOpen(true); };
  const openEditItem = (idx: number) => { setEditingIndex(idx); setLineItemModalOpen(true); };

  const handleSaveLineItem = (item: ServiceOrderLineItem, addAnother: boolean) => {
    if (editingIndex !== null) {
      setLineItems(prev => prev.map((li, i) => i === editingIndex ? item : li));
    } else {
      setLineItems(prev => [...prev, item]);
    }
    if (!addAnother) setLineItemModalOpen(false);
    setEditingIndex(null);
  };

  const removeItem = (idx: number) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  // ── Totals (same logic as ServiceOrderModal) ──────────────────────────────
  const total = (() => {
    if (!isInclusiveTax) return subTotal(lineItems);
    return lineItems.reduce((s, item) => {
      const lineAmt = item.quantity * item.rate;
      if (!item.discount) return s + lineAmt;
      if (item.discountType !== '$') return s + lineAmt * (1 - item.discount / 100);
      const taxInfo = zohoTaxes.find(t => t.tax_id === item.tax_id);
      return s + lineAmt - item.discount * (1 + (taxInfo?.tax_percentage ?? 0) / 100);
    }, 0);
  })();

  const adjustmentNum = parseFloat(adjustment) || 0;

  const taxBreakdownMap = (() => {
    const taxMap = new Map<string, { name: string; pct: number; amount: number }>();
    for (const item of lineItems) {
      if (!item.tax_id) continue;
      const lineAmt = item.quantity * item.rate;
      const discountAmt = item.discount
        ? item.discountType === '%' ? lineAmt * (item.discount / 100) : item.discount
        : 0;
      const net = lineAmt - discountAmt;
      const taxInfo = zohoTaxes.find(t => t.tax_id === item.tax_id);
      const groupPct = taxInfo?.tax_percentage ?? 0;
      const preTaxNet = isInclusiveTax
        ? (item.discountType === '$' && item.discount
            ? lineAmt / (1 + groupPct / 100) - item.discount
            : net / (1 + groupPct / 100))
        : net;
      if (taxInfo?.is_tax_group && taxInfo.taxes?.length) {
        for (const comp of taxInfo.taxes) {
          const compAmt = isInclusiveTax
            ? preTaxNet * (comp.tax_percentage / 100)
            : net * (comp.tax_percentage / 100);
          const existing = taxMap.get(comp.tax_id);
          if (existing) { existing.amount += compAmt; }
          else { taxMap.set(comp.tax_id, { name: `${comp.tax_name} [${comp.tax_percentage}%]`, pct: comp.tax_percentage, amount: compAmt }); }
        }
      } else {
        const pct = taxInfo?.tax_percentage ?? 0;
        const taxAmt = isInclusiveTax ? preTaxNet * (pct / 100) : net * (pct / 100);
        const existing = taxMap.get(item.tax_id);
        if (existing) { existing.amount += taxAmt; }
        else {
          const label = taxInfo ? `${taxInfo.tax_name} [${taxInfo.tax_percentage}%]` : (item.tax_name ?? item.tax_id);
          taxMap.set(item.tax_id, { name: label, pct, amount: taxAmt });
        }
      }
    }
    return taxMap;
  })();

  const taxBreakdownRows = Array.from(taxBreakdownMap.values());
  const totalTaxAmt = taxBreakdownRows.reduce((s, r) => s + r.amount, 0);
  const grandTotal  = isInclusiveTax ? total + adjustmentNum : total + totalTaxAmt + adjustmentNum;

  const effectiveTermDays = paymentTermsDays === -1 ? customTermDays : paymentTermsDays;
  const termLabel = paymentTermsDays === -1
    ? (customTermDays > 0 ? tr('components.ServiceOrderInline.net', { customTermDays }) : tr('components.ServiceOrderInline.custom'))
    : (PAYMENT_TERMS.find(t => t.days === paymentTermsDays)?.label ?? tr('components.ServiceOrderInline.net3'));

  // Editing an existing order (initialDraft provided) already has valid,
  // individually-priced/taxed line items pulled straight from Zoho — a
  // pricelist is only needed to auto-suggest rates for NEW line items, not to
  // save an order that already has its own rates set, so it isn't required here.
  const isEditMode = !!initialDraft;
  const canConfirm =
    !!customerName?.trim() &&
    !!soDate?.trim() &&
    !!serviceMonth?.trim() &&
    (isEditMode || !!pricelistId?.trim()) &&
    lineItems.length > 0 &&
    // Some Zoho SO line items carry their text in `description` only, with an
    // empty `name` — checking `name` alone wrongly treated those as
    // blank/invalid items and permanently blocked Save.
    lineItems.some(i => i.name.trim() || i.description.trim()) &&
    // Same Zoho SO quirk as the name/description fallback above — some line
    // items carry a human-readable `tax_name` but never got a `tax_id`
    // extracted, so require one or the other rather than specifically the
    // raw ID, or a pre-existing, already-taxed edit-mode item permanently
    // blocks Save for a field the UI doesn't even surface.
    !lineItems.some(i => (i.name.trim() || i.description.trim()) && !i.tax_id && !i.tax_name);

  const buildDraft = (action: 'confirm' | 'draft' | 'send'): ServiceOrderDraft => ({
    soNumber: soNumberMode !== 'auto' && soNumber.trim() && soNumber.trim() !== 'SO-'
      ? soNumber.trim() : undefined,
    soDate,
    paymentTerms:      effectiveTermDays,
    paymentTermsLabel: termLabel,
    serviceMonth:              serviceMonthsFromZoho ? (serviceMonth || undefined) : undefined,
    serviceMonthCustomFieldId: serviceMonthCfIdRef.current || undefined,
    isInclusiveTax,
    adjustment:  adjustmentNum || undefined,
    pricelistId: pricelistId.trim() || undefined,
    lineItems,
    notes: soNotes,
    terms: soTerms || undefined,
    total: grandTotal,
    taxAmount: totalTaxAmt,
    action,
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <IonSpinner name="bubbles" />
        <span>{tr('components.ServiceOrderInline.loadingOrderData')}</span>
      </div>
    );
  }

  return (
    <>
      {/* ── Order Details ──────────────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{tr('components.ServiceOrderInline.orderDetails')}</div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.ServiceOrderInline.customerName')}</label>
          <input className={styles.input} value={customerName || 'Guest'} readOnly />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel} style={soNumberError ? { color: 'var(--ion-color-danger)' } : undefined}>{tr('components.ServiceOrderInline.serviceOrder')}
          </label>
          <div className={styles.inputRow}>
            <input
              className={`${styles.input} ${styles.inputFlex}`}
              value={soNumberMode === 'auto' ? (nextSoNumber ? 'SO-' + nextSoNumber : '') : soNumber}
              placeholder={soNumberMode === 'manual-once' ? tr('components.ServiceOrderInline.so') : tr('components.ServiceOrderInline.autoAssignedByZoho')}
              readOnly={soNumberMode !== 'manual-once'}
              style={soNumberMode === 'auto' ? { color: 'var(--ion-color-medium)' } : undefined}
              onClick={() => { if (soNumberMode !== 'manual-once') setSoNumberAlertOpen(true); }}
              onChange={e => {
                if (soNumberMode !== 'manual-once') return;
                let val = e.target.value;
                if (val && !val.startsWith('SO-')) val = 'SO-' + val.replace(/^SO-*/i, '');
                setSoNumber(val);
              }}
            />
            <button className={styles.iconBtn} onClick={() => setSoNumberAlertOpen(true)}>
              <IonIcon icon={settingsOutline} />
            </button>
          </div>
          {soNumberError && <p className={styles.fieldError}>{soNumberError}</p>}
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.ServiceOrderInline.referenceBookingId')}</label>
          <input className={styles.input} value={bookingId} readOnly placeholder={tr('components.ServiceOrderInline.wixBookingId')} />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.ServiceOrderInline.serviceOrderDate')}</label>
          <input
            type="date"
            className={styles.input}
            value={soDate}
            onChange={e => setSoDate(e.target.value || todayISO())}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.ServiceOrderInline.paymentTerms')}</label>
          <div ref={termsDropWrapRef} className={styles.customDropdown}>
            <div
              ref={termsDropTriggerRef}
              role="button"
              tabIndex={0}
              className={`${styles.pickerBtn} ${styles.customDropdownTrigger}`}
              onClick={handleTermsDropOpen}
              onKeyDown={e => e.key === 'Enter' && handleTermsDropOpen()}
            >
              <span>{termLabel}</span>
              <IonIcon icon={chevronDownOutline} className={`${styles.pickerCaret} ${termsDropOpen ? styles.dropdownCaretOpen : ''}`} />
            </div>
            {termsDropOpen && ReactDOM.createPortal(
              <div
                ref={termsDropListRef}
                className={styles.customDropdownList}
                style={{ top: termsDropPos.top, left: termsDropPos.left, width: termsDropPos.width }}
              >
                {PAYMENT_TERMS.map(t => (
                  <button key={t.days} type="button"
                    className={`${styles.customDropdownItem} ${paymentTermsDays === t.days ? styles.customDropdownItemActive : ''}`}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { setPaymentTermsDays(t.days); setTermsDropOpen(false); }}>
                    {t.label}
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
        </div>

        {paymentTermsDays === -1 && (
          <div className={styles.field}>
            <label className={styles.fieldLabel}>{tr('components.ServiceOrderInline.customDaysNet')}</label>
            <input
              type="number"
              min="1"
              className={styles.input}
              placeholder="e.g. 45"
              value={customTermDays || ''}
              onChange={e => setCustomTermDays(Number(e.target.value ?? 0))}
            />
          </div>
        )}
      </div>

      {/* ── Order Settings ─────────────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.ServiceOrderInline.serviceMonth')}</label>
          <div ref={monthDropWrapRef} className={styles.customDropdown}>
            <div
              ref={monthDropTriggerRef}
              role="button"
              tabIndex={0}
              className={`${styles.pickerBtn} ${styles.customDropdownTrigger}`}
              onClick={handleMonthDropOpen}
              onKeyDown={e => e.key === 'Enter' && handleMonthDropOpen()}
            >
              <span>{serviceMonth || tr('components.ServiceOrderInline.none')}</span>
              <IonIcon icon={chevronDownOutline} className={`${styles.pickerCaret} ${monthDropOpen ? styles.dropdownCaretOpen : ''}`} />
            </div>
            {monthDropOpen && ReactDOM.createPortal(
              <div
                ref={monthDropListRef}
                className={styles.customDropdownList}
                style={{ top: monthDropPos.top, left: monthDropPos.left, width: monthDropPos.width }}
              >
                <button type="button" className={styles.customDropdownItem}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => { setServiceMonth(''); setMonthDropOpen(false); }}>{tr('components.ServiceOrderInline.none')}
                </button>
                {serviceMonths.map(m => (
                  <button key={m} type="button"
                    className={`${styles.customDropdownItem} ${serviceMonth === m ? styles.customDropdownItemActive : ''}`}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { setServiceMonth(m); setMonthDropOpen(false); }}>
                    {m}
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.ServiceOrderInline.tax')}</label>
          <div className={styles.radioRow}>
            <label className={styles.radioOption}>
              <input
                type="radio"
                name="soInlineTax"
                checked={!isInclusiveTax}
                onChange={() => setIsInclusiveTax(false)}
              />{tr('components.ServiceOrderInline.exclusive')}
            </label>
            <label className={styles.radioOption}>
              <input
                type="radio"
                name="soInlineTax"
                checked={isInclusiveTax}
                onChange={() => setIsInclusiveTax(true)}
              />{tr('components.ServiceOrderInline.inclusive')}
            </label>
          </div>
        </div>
      </div>

      {/* ── Line Items ─────────────────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{tr('components.ServiceOrderInline.lineItems')}</div>

        {/* Existing items — clicking one opens inline edit form */}
        {lineItems.map((item, index) => {
          const lineAmt = item.quantity * item.rate;
          const rowTaxPct = zohoTaxes.find(t => t.tax_id === item.tax_id)?.tax_percentage ?? 0;
          const netAmt = (() => {
            if (!item.discount) return lineAmt;
            if (!isInclusiveTax || item.discountType !== '$') {
              const d = item.discountType === '%' ? lineAmt * (item.discount / 100) : item.discount;
              return lineAmt - d;
            }
            return lineAmt - item.discount * (1 + rowTaxPct / 100);
          })();
          const isEditing = lineItemModalOpen && editingIndex === index;
          return (
            <React.Fragment key={index}>
              <div
                className={`${styles.lineItem} ${isEditing ? styles.lineItemEditing : ''}`}
                onClick={() => !isEditing && openEditItem(index)}
              >
                <button className={styles.lineItemRemove} onClick={e => { e.stopPropagation(); removeItem(index); setLineItemModalOpen(false); }}>
                  <IonIcon icon={trashOutline} />
                </button>
                <div className={styles.lineItemBody}>
                  <div className={styles.lineItemName}>{item.name}</div>
                  <div className={styles.lineItemMeta}>
                    {item.quantity} × ${item.rate.toFixed(2)}
                    {item.discount ? tr('components.ServiceOrderInline.off', { v1: item.discountType === '$' ? `$${item.discount}` : `${item.discount}%` }) : ''}
                    {item.tax_name ? ` · ${item.tax_name}` : ''}
                  </div>
                  {item.description && <div className={styles.lineItemDesc}>{item.description}</div>}
                </div>
                <div className={styles.lineItemAmt}>${netAmt.toFixed(2)}</div>
              </div>
              {isEditing && (
                <LineItemInline
                  onSave={handleSaveLineItem}
                  onCancel={() => { setLineItemModalOpen(false); setEditingIndex(null); }}
                  initial={item}
                  zohoItems={zohoItems}
                  zohoTaxes={zohoTaxes}
                  loadingItems={loadingItems}
                  loadingTaxes={loadingTaxes}
                  pricelists={pricelists}
                  pricelistId={pricelistId}
                  onPricelistChange={setPricelistId}
                  loadingPricelists={loadingPricelists}
                  isInclusiveTax={isInclusiveTax}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* Add button — hidden while editing or adding */}
        {!lineItemModalOpen && (
          <button className={styles.addItemBtn} onClick={openAddItem}>
            <IonIcon icon={addCircleOutline} />{tr('components.ServiceOrderInline.addLineItem')}
          </button>
        )}

        {/* Inline add form */}
        {lineItemModalOpen && editingIndex === null && (
          <LineItemInline
            onSave={handleSaveLineItem}
            onCancel={() => setLineItemModalOpen(false)}
            initial={null}
            zohoItems={zohoItems}
            zohoTaxes={zohoTaxes}
            loadingItems={loadingItems}
            loadingTaxes={loadingTaxes}
            pricelists={pricelists}
            pricelistId={pricelistId}
            onPricelistChange={setPricelistId}
            loadingPricelists={loadingPricelists}
            isInclusiveTax={isInclusiveTax}
          />
        )}

        {lineItems.length > 0 && (
          <div className={styles.totals}>
            <div className={styles.totalRow}>
              <span>{tr('components.ServiceOrderInline.subTotal')}
                <span className={styles.taxNote}> {isInclusiveTax ? tr('components.ServiceOrderInline.taxIncl') : tr('components.ServiceOrderInline.taxExcl')}</span>
              </span>
              <span>${total.toFixed(2)}</span>
            </div>
            {taxBreakdownRows.map(r => (
              <div key={r.name} className={styles.totalRow}>
                <span>{r.name}</span>
                <span>{r.amount.toFixed(2)}</span>
              </div>
            ))}
            {taxBreakdownRows.length > 0 && (
              <div className={styles.totalRow}>
                <span>{tr('components.ServiceOrderInline.totalTax')}</span>
                <span className={styles.taxTotalVal}>{totalTaxAmt.toFixed(2)}</span>
              </div>
            )}
            <div className={styles.totalRow}>
              <span>{tr('components.ServiceOrderInline.adjustment')}</span>
              <input
                type="text"
                inputMode="decimal"
                className={styles.adjustInput}
                value={adjustment}
                placeholder="0.00"
                onChange={e => setAdjustment(e.target.value)}
                onBlur={e => {
                  const n = parseFloat(e.target.value);
                  setAdjustment(isNaN(n) ? '' : String(n));
                }}
              />
            </div>
            <div className={`${styles.totalRow} ${styles.grandTotal}`}>
              <span>{tr('components.ServiceOrderInline.total')}</span>
              <span>${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Notes & Terms ──────────────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.ServiceOrderInline.customerNotes')}</label>
          <textarea
            className={styles.textarea}
            rows={3}
            placeholder={tr('components.ServiceOrderInline.notesVisibleToTheCustomer')}
            value={soNotes}
            onChange={e => setSoNotes(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.ServiceOrderInline.termsConditions')}</label>
          <textarea
            className={styles.textarea}
            rows={3}
            placeholder={tr('components.ServiceOrderInline.paymentPolicyCancellationTerms')}
            value={soTerms}
            onChange={e => setSoTerms(e.target.value)}
          />
        </div>
      </div>

      {/* ── Actions ────────────────────────────────────────────────── */}
      <div className={styles.actions}>
        <button
          className={styles.confirmBtn}
          disabled={!canConfirm}
          onClick={() => onConfirm(buildDraft('confirm'))}
        >
          <IonIcon icon={receiptOutline} />
          {confirmLabel}
        </button>
        <button className={styles.draftBtn} onClick={() => onConfirm(buildDraft('draft'))}>{tr('components.ServiceOrderInline.saveAsDraft')}
        </button>
      </div>

      {/* ── Sub-modals ─────────────────────────────────────────────── */}
      <GlassPicker
        isOpen={soNumberAlertOpen}
        onClose={() => setSoNumberAlertOpen(false)}
        title={tr('components.ServiceOrderInline.salesOrder')}
        options={[
          {
            label: tr('components.ServiceOrderInline.continueAutoGeneratingSalesOrderNumbers'),
            value: 'auto',
            content: (
              <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--ion-color-medium)', marginBottom: 4 }}>{tr('components.ServiceOrderInline.prefix')}</div>
                  <input readOnly value="SO-" style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '8px 10px', fontSize: 14, color: 'var(--ion-color-medium)' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--ion-color-medium)', marginBottom: 4 }}>{tr('components.ServiceOrderInline.nextNumber')}</div>
                  <input
                    value={nextSoNumber}
                    onChange={e => setNextSoNumber(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '8px 10px', fontSize: 14, color: 'var(--ion-text-color)' }}
                  />
                </div>
              </div>
            ),
          },
          { label: tr('components.ServiceOrderInline.iWillAddThemManuallyEach'), value: 'manual-each' },
          { label: tr('components.ServiceOrderInline.iWillAddThemManuallyOnly'), value: 'manual-once' },
        ]}
        value={soNumberMode}
        radioMode
        onSelect={v => {
          const mode = v as 'auto' | 'manual-each' | 'manual-once';
          setSoNumberMode(mode);
          if (mode === 'auto') {
            setSoNumber('');
          } else if (!soNumber || soNumber === 'SO-') {
            setSoNumber(nextSoNumber ? 'SO-' + nextSoNumber : 'SO-');
          }
        }}
        onSave={async () => {
          if (soNumberMode === 'auto' && nextSoNumber) {
            try {
              await resetSoNumberSequence(`SO-${nextSoNumber}`);
            } catch (e) {
              alert(tr('components.ServiceOrderInline.failedToResetSoSequence', { v1: e instanceof Error ? e.message : String(e) }));
            }
          }
          setSoNumberAlertOpen(false);
        }}
      />

    </>
  );
};

export default ServiceOrderInline;
